import { useState } from "react";
import { Building2, MapPin, Warehouse, Plus, Pencil } from "lucide-react";
import Modal from "./common/Modal";
import AddEditBranchForm from "./forms/AddEditBranchForm";
import { useGetAllBranches } from "../service/branchService";
import { Branch } from "../types/branchType";

const BranchList = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<Branch | undefined>(
    undefined,
  );

  const { data } = useGetAllBranches({
    params: {
      search: "",
      isActive: true,
      page: 0,
      size: 10,
      sortBy: "branchName",
      sortDir: "asc",
    },
  });

  const isWarehouse = (type: string) => type === "WAREHOUSE";

  const handleAddClick = () => {
    setSelectedBranch(undefined);
    setIsModalOpen(true);
  };

  const handleEditClick = (branch: Branch, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedBranch(branch);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedBranch(undefined);
  };

  return (
    <>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[#21314A]">
              Branches &amp; Warehouses
            </h2>
            <p className="text-sm text-[#8A9BB5] mt-0.5">
              Manage your branch and warehouse locations
            </p>
          </div>
          {/* <button
            onClick={handleAddClick}
            className="h-11 rounded-[12px] bg-[#1A5EF9] px-5 text-white font-medium text-sm flex items-center gap-2 hover:bg-[#0E4FDE] transition-colors"
          >
            <Plus size={16} />
            Add Branch
          </button> */}
        </div>

        {/* Grid */}
        {data?.content && data.content.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {data.content.map((branch) => (
              <div
                key={branch.branchId}
                className="rounded-[20px] border border-[#E8EEF9] bg-white shadow-[0_8px_24px_rgba(26,94,249,0.06)] p-5 hover:shadow-[0_12px_32px_rgba(26,94,249,0.12)] hover:border-[#BFD1FF] transition-all duration-200 cursor-pointer"
              >
                {/* Icon + badge + edit */}
                <div className="flex items-start justify-between mb-4">
                  <div
                    className={`w-11 h-11 rounded-[12px] flex items-center justify-center ${
                      isWarehouse(branch.type) ? "bg-[#EEF3FF]" : "bg-[#F0F4FF]"
                    }`}
                  >
                    {isWarehouse(branch.type) ? (
                      <Warehouse className="w-5 h-5 text-[#1A5EF9]" />
                    ) : (
                      <Building2 className="w-5 h-5 text-[#1A5EF9]" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[11px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wide ${
                        isWarehouse(branch.type)
                          ? "bg-[#EEF3FF] text-[#1A5EF9]"
                          : "bg-[#F0F4FF] text-[#1A5EF9]"
                      }`}
                    >
                      {branch.type}
                    </span>
                    <button
                      onClick={(e) => handleEditClick(branch, e)}
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-[#8A9BB5] hover:bg-[#F0F4FF] hover:text-[#1A5EF9] transition-colors"
                      title="Edit branch"
                      aria-label="Edit branch"
                    >
                      <Pencil size={14} />
                    </button>
                  </div>
                </div>

                {/* Name */}
                <h3 className="text-[15px] font-semibold text-[#1A2B4A] mb-2 truncate">
                  {branch.branchName}
                </h3>

                {/* Location */}
                <div className="flex items-center gap-1.5 text-[#8A9BB5] text-sm">
                  <MapPin size={13} className="flex-shrink-0" />
                  <span className="truncate">{branch.location || "—"}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-[20px] border border-[#E8EEF9] bg-white py-16 text-center">
            <Building2 className="w-10 h-10 text-[#D0DAEE] mx-auto mb-3" />
            <p className="text-sm font-medium text-[#52627C]">
              No branches added yet
            </p>
            <p className="text-xs text-[#8A9BB5] mt-1">
              Click "Add Branch" to get started
            </p>
          </div>
        )}
      </div>

      <Modal open={isModalOpen} setOpen={setIsModalOpen}>
        <AddEditBranchForm
          onClose={handleModalClose}
          editBranch={selectedBranch}
        />
      </Modal>
    </>
  );
};

export default BranchList;
